import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff, Lock } from "lucide-react";
import { DialogFooter, Button, Input, Label } from "../../../components";
import { CustomModal } from "../../../components/modal";
import { FormErrorText } from "../../../components/form";
import { useToast } from "../../../components/toast-container";
import { getErrorMessage } from "../../../utils/utils";
import { useChangePassword } from "../hooks/useChangePassword";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { changePassword, loading, reset: resetChangePassword } = useChangePassword();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Zod schema for validation
  const changePasswordSchema = z
    .object({
      currentPassword: z.string().min(1, t("profile.current_password_required")),
      newPassword: z.string().min(8, t("profile.password_too_short")),
      confirmPassword: z.string().min(1, t("profile.confirm_password_required")),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("profile.passwords_do_not_match"),
      path: ["confirmPassword"],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
      message: t("profile.same_as_current"),
      path: ["newPassword"],
    });

  type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await changePassword({
        old_password: data.currentPassword,
        new_password: data.newPassword,
        new_password_confirmation: data.confirmPassword,
      });
      addToast({
        type: "success",
        title: "Success!",
        message: t("profile.password_changed_successfully"),
      });
      onOpenChange(false);
      reset();
    } catch (error: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getErrorMessage(error, t("profile.password_change_failed")),
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    reset();
    resetChangePassword();
  };

  return (
    <CustomModal
      isOpen={open}
      onClose={handleCancel}
      title={t("profile.change_password")}
      maxWidth="xs"
      paperClassName="w-full max-w-[500px] rounded-[10px] dark:bg-[#212936]"
      titleClassName="px-[24.8px] pt-[24.8px] text-lg leading-[18px] font-semibold text-[#0A0A0A] dark:text-white font-nunito"
      contentClassName="p-[24.8px] pt-4"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5"
      >
        {/* Current Password */}
        <div className="space-y-2">
          <Label
            htmlFor="currentPassword"
            className="text-sm leading-[14px] font-semibold text-[#0A0A0A] dark:text-white font-nunito"
          >
            {t("profile.current_password")}
          </Label>
          <Controller
            name="currentPassword"
            control={control}
            render={({ field }) => (
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                startIcon={<Lock className="h-4 w-4" />}
                endIcon={showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                onEndIconClick={() => setShowCurrentPassword((current) => !current)}
                endIconAriaLabel={showCurrentPassword ? t("auth.hide_password") : t("auth.show_password")}
                value={field.value}
                onChange={field.onChange}
                disabled={loading}
                aria-invalid={errors.currentPassword ? "true" : "false"}
                aria-describedby={errors.currentPassword ? "currentPassword-error" : undefined}
                className={`h-11 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-emerald-500 dark:focus:ring-emerald-500 ${
                  errors.currentPassword ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""
                }`}
              />
            )}
          />
          {errors.currentPassword && (
            <FormErrorText id="currentPassword-error" message={errors.currentPassword.message} />
          )}
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <Label
            htmlFor="newPassword"
            className="text-sm leading-[14px] font-semibold text-[#0A0A0A] dark:text-white font-nunito"
          >
            {t("profile.new_password")}
          </Label>
          <Controller
            name="newPassword"
            control={control}
            render={({ field }) => (
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                startIcon={<Lock className="h-4 w-4" />}
                endIcon={showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                onEndIconClick={() => setShowNewPassword((current) => !current)}
                endIconAriaLabel={showNewPassword ? t("auth.hide_password") : t("auth.show_password")}
                value={field.value}
                onChange={field.onChange}
                disabled={loading}
                aria-invalid={errors.newPassword ? "true" : "false"}
                aria-describedby={errors.newPassword ? "newPassword-error" : undefined}
                className={`h-11 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-emerald-500 dark:focus:ring-emerald-500 ${
                  errors.newPassword ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""
                }`}
              />
            )}
          />
          {errors.newPassword && (
            <FormErrorText id="newPassword-error" message={errors.newPassword.message} />
          )}
          {!errors.newPassword && (
            <p className="text-xs text-[#4A5565] dark:text-gray-400 font-nunito">
              • {t("profile.password_requirement")}
            </p>
          )}
        </div>

        {/* Confirm New Password */}
        <div className="space-y-2">
          <Label
            htmlFor="confirmPassword"
            className="text-sm leading-[14px] font-semibold text-[#0A0A0A] dark:text-white font-nunito"
          >
            {t("profile.confirm_new_password")}
          </Label>
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                startIcon={<Lock className="h-4 w-4" />}
                endIcon={showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                onEndIconClick={() => setShowConfirmPassword((current) => !current)}
                endIconAriaLabel={showConfirmPassword ? t("auth.hide_password") : t("auth.show_password")}
                value={field.value}
                onChange={field.onChange}
                disabled={loading}
                aria-invalid={errors.confirmPassword ? "true" : "false"}
                aria-describedby={errors.confirmPassword ? "confirmPassword-error" : undefined}
                className={`h-11 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-emerald-500 dark:focus:ring-emerald-500 ${
                  errors.confirmPassword ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""
                }`}
              />
            )}
          />
          {errors.confirmPassword && (
            <FormErrorText id="confirmPassword-error" message={errors.confirmPassword.message} />
          )}
        </div>

        <DialogFooter className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            className="h-[36px] py-[8px] px-[16.8px] rounded-[8px] border border-[rgba(0,0,0,0.1)] dark:border-[#394150] bg-white dark:bg-[#394150] text-[#0A0A0A] dark:text-white text-sm font-semibold font-nunito hover:bg-gray-50 dark:hover:bg-[#4A5565]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="h-[36px] py-[8px] px-[16px] rounded-[8px] bg-[#00D6A8] hover:bg-[#00C29B] text-[#004232] text-sm font-semibold font-nunito"
          >
            {loading ? t("common.processing") || "Processing..." : t("profile.change_password")}
          </Button>
        </DialogFooter>
      </form>
    </CustomModal>
  );
};
